import { useTranslation } from 'react-i18next';
import { Accordion, ActionIcon, Badge, Box, Group, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconFileText, IconFolder } from '@tabler/icons-react';
import SignatureBadge from '@/components/app/deals/SignatureBadge';
import { useDownloadDocument } from '@/hooks/useDeals';
import { countTreeDocuments } from '@/lib/dealDocuments';
import { signatureStateView } from '@/lib/dealStatus';
import type { DocumentTreeNode, SignatureRole } from '@/types/deals';

interface DealDocumentsProps {
  readonly nodes: readonly DocumentTreeNode[];
}

type T = ReturnType<typeof useTranslation>['t'];
type DownloadDoc = ReturnType<typeof useDownloadDocument>;

// Подписи показываем в стабильном порядке ролей (NOT_REQUIRED/отсутствие SignatureBadge скрывает сам).
const SIGN_ROLES: readonly SignatureRole[] = ['SELLER', 'BUYER', 'AGENT', 'FORWARDER', 'BANK'];

// Строка одного документа дерева (узел DOCUMENT): иконка + имя + дата + подписи ролей + скачивание.
// Read-only. Вынесена на уровень модуля (не пересоздаётся на каждый рендер; key — на месте вызова).
function DocTreeRow({ node, t, download }: { readonly node: DocumentTreeNode; readonly t: T; readonly download: DownloadDoc }) {
  // Только роли с реальной подписью (фильтруем по состоянию заранее, иначе рисуется пустой контейнер).
  const signedRoles = SIGN_ROLES.filter((role) => signatureStateView(node.signatures?.[role]?.state) !== null);
  const isDownloading = download.isPending && download.variables === node.documentId;

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
      <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
        <ThemeIcon size={32} radius="md" variant="light" color="gray">
          <IconFileText size={18} />
        </ThemeIcon>
        <Box style={{ minWidth: 0 }}>
          <Text fz="sm" fw={500} lineClamp={2}>
            {node.name ?? t('app.deals.docs.untitled')}
          </Text>
          {node.source?.createdDate ? (
            <Text fz="xs" c="gray.6">
              {node.source.createdDate}
            </Text>
          ) : null}
          {signedRoles.length > 0 ? (
            <Group gap={4} wrap="wrap" mt={4}>
              {signedRoles.map((role) => (
                <SignatureBadge key={role} role={role} info={node.signatures?.[role]} />
              ))}
            </Group>
          ) : null}
        </Box>
      </Group>
      {node.documentId != null ? (
        <Tooltip label={t('app.deals.docs.download')}>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label={t('app.deals.docs.download')}
            loading={isDownloading}
            onClick={() => download.mutate(node.documentId!)}
          >
            <IconDownload size={18} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </Group>
  );
}

// Единый архив документов сделки (режим «Документы»). Верхние группы (Основные/Закрывающие/Перевозка)
// — сворачиваемые секции (Accordion, открыты по умолчанию) со счётчиком документов; внутри —
// рекурсивная иерархия DATE → VEHICLE → DOCUMENT. Read-only. Источник — GET /documents/tree.
export default function DealDocuments({ nodes }: DealDocumentsProps) {
  const { t } = useTranslation();
  const download = useDownloadDocument();

  // Вложенный узел внутри секции-группы: DOCUMENT → строка; DATE/VEHICLE → подзаголовок + дети.
  function renderNode(node: DocumentTreeNode, key: string) {
    if (node.nodeType === 'DOCUMENT') {
      return <DocTreeRow key={key} node={node} t={t} download={download} />;
    }
    const label = node.name ?? node.deliveryDate ?? node.vehicleRegnum ?? '';
    const children = node.items ?? [];
    return (
      <Box key={key}>
        <Group gap="xs" wrap="nowrap" mb="xs">
          <ThemeIcon size={20} radius="sm" variant="transparent" color="gray">
            <IconFolder size={16} />
          </ThemeIcon>
          <Text fz="sm" fw={600} c="gray.8">
            {label}
          </Text>
        </Group>
        <Stack gap="sm" pl="md">
          {children.map((child, index) => renderNode(child, `${key}.${index}`))}
        </Stack>
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Text fz="sm" c="gray.6">
        {t('app.deals.docs.empty')}
      </Text>
    );
  }

  // Верхний уровень дерева — это группы (Основные/Закрывающие/Перевозка). Отдельные документы без
  // группы (редкий случай) показываем плоским списком над аккордеоном.
  const looseDocs = nodes.filter((n) => n.nodeType === 'DOCUMENT');
  const groups = nodes.filter((n) => n.nodeType !== 'DOCUMENT');
  // Открываем все группы по умолчанию (видно всё), но даём свернуть для навигации по длинному архиву.
  const openValues = groups.map((_, index) => `g${index}`);

  return (
    <Stack gap="md">
      {looseDocs.length > 0 ? (
        <Stack gap="sm">
          {looseDocs.map((doc, index) => (
            <DocTreeRow key={`loose-${index}`} node={doc} t={t} download={download} />
          ))}
        </Stack>
      ) : null}

      {groups.length > 0 ? (
        <Accordion multiple defaultValue={openValues} order={6} variant="separated" radius="md">
          {groups.map((group, gi) => {
            const label = group.name ?? group.deliveryDate ?? group.vehicleRegnum ?? '';
            const count = countTreeDocuments(group.items ?? []);
            const children = group.items ?? [];
            return (
              <Accordion.Item key={`g${gi}`} value={`g${gi}`}>
                <Accordion.Control
                  icon={
                    <ThemeIcon size={24} radius="sm" variant="light" color="gray">
                      <IconFolder size={16} />
                    </ThemeIcon>
                  }
                >
                  <Group gap="xs" wrap="nowrap">
                    <Text fz="sm" fw={600} c="gray.8">
                      {label}
                    </Text>
                    {count > 0 ? (
                      <Badge variant="light" color="gray" size="sm" radius="sm">
                        {count}
                      </Badge>
                    ) : null}
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="sm">{children.map((child, index) => renderNode(child, `g${gi}.${index}`))}</Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : null}
    </Stack>
  );
}
